// Pendoの制限 (ペイロード全体で512バイト) からマージンを取った値
const PENDO_LIMIT_BYTES = 480;

const CRITICAL_PROPERTY_MAX_BYTES = 150;
const IMPORTANT_PROPERTY_MAX_BYTES = 100;
const LOW_PRIORITY_PROPERTY_MAX_BYTES = 50;
const DEFAULT_PROPERTY_MAX_BYTES = 80;
const CRITICAL_PROP_MIN_BYTES = 50;

const truncateToBytes = (str: string, maxBytes: number): string => {
  const encoder = new TextEncoder();
  const encoded = encoder.encode(str);
  if (encoded.length <= maxBytes) return str;

  const ELLIPSIS = '...';
  const targetBytes = maxBytes - ELLIPSIS.length;

  let result = '';
  let currentBytes = 0;

  for (const char of str) {
    const charBytes = encoder.encode(char).length;
    if (currentBytes + charBytes > targetBytes) {
      break;
    }
    result += char;
    currentBytes += charBytes;
  }

  return result + ELLIPSIS;
};

const getJsonByteSize = (obj: unknown): number => {
  return new TextEncoder().encode(JSON.stringify(obj)).length;
};

/**
 * Pendo送信用にペイロード全体を512バイト以下にサニタイズ
 * 3フェーズでデータを削減して制限内に収める
 *
 * 優先度:
 * - Critical: message, stack, src
 * - Important: status, statusText, reason, filename, lineno, colno, data
 * - Low: type, tagName, raw, id, className
 */
const sanitizeForPendo = (
  eventType: string,
  errorInfo: Record<string, unknown>,
  timestamp: number,
): { event: string; timestamp: number; [key: string]: unknown } => {
  const criticalKeys = ['message', 'stack', 'src'];
  const importantKeys = ['status', 'statusText', 'reason', 'filename', 'lineno', 'colno', 'data'];
  const lowPriorityKeys = ['type', 'tagName', 'raw', 'id', 'className'];

  // Phase 1: 全プロパティのサイズを削減
  let sanitized: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(errorInfo)) {
    let maxSize = DEFAULT_PROPERTY_MAX_BYTES;

    if (criticalKeys.includes(key)) {
      maxSize = CRITICAL_PROPERTY_MAX_BYTES;
    } else if (importantKeys.includes(key)) {
      maxSize = IMPORTANT_PROPERTY_MAX_BYTES;
    } else if (lowPriorityKeys.includes(key)) {
      maxSize = LOW_PRIORITY_PROPERTY_MAX_BYTES;
    }

    if (typeof value === 'string') {
      sanitized[key] = truncateToBytes(value, maxSize);
    } else if (typeof value === 'object' && value !== null) {
      try {
        const serialized = JSON.stringify(value);
        sanitized[key] = truncateToBytes(serialized, maxSize);
      } catch {
        sanitized[key] = 'unserializable';
      }
    } else {
      sanitized[key] = value;
    }
  }

  let payload = { event: eventType, ...sanitized, timestamp };

  // Phase 2: Critical以外を削除
  if (getJsonByteSize(payload) > PENDO_LIMIT_BYTES) {
    const reducedSanitized: Record<string, unknown> = {};
    for (const key of criticalKeys) {
      if (key in sanitized) {
        reducedSanitized[key] = sanitized[key];
      }
    }
    sanitized = reducedSanitized;
    payload = { event: eventType, ...sanitized, timestamp };
  }

  // Phase 3: Criticalのサイズをさらに削減
  if (getJsonByteSize(payload) > PENDO_LIMIT_BYTES) {
    const existCriticalKeys = criticalKeys.filter((key) => key in sanitized);

    if (existCriticalKeys.length > 0) {
      const basePayloadWithKeys: Record<string, unknown> = { event: eventType, timestamp };
      for (const key of existCriticalKeys) {
        basePayloadWithKeys[key] = '';
      }
      const basePayloadSize = getJsonByteSize(basePayloadWithKeys);
      const availableBytes = PENDO_LIMIT_BYTES - basePayloadSize;
      const perPropertyBytes = Math.floor(availableBytes / existCriticalKeys.length);

      if (sanitized.message && typeof sanitized.message === 'string') {
        const messageSizeTarget = Math.max(CRITICAL_PROP_MIN_BYTES, perPropertyBytes);
        sanitized.message = truncateToBytes(sanitized.message, messageSizeTarget);
      }
      if (sanitized.stack && typeof sanitized.stack === 'string') {
        const stackSizeTarget = Math.max(CRITICAL_PROP_MIN_BYTES, perPropertyBytes);
        sanitized.stack = truncateToBytes(sanitized.stack, stackSizeTarget);
      }
      if (sanitized.src && typeof sanitized.src === 'string') {
        const srcSizeTarget = Math.max(CRITICAL_PROP_MIN_BYTES, perPropertyBytes);
        sanitized.src = truncateToBytes(sanitized.src, srcSizeTarget);
      }
      payload = { event: eventType, ...sanitized, timestamp };
    }
  }

  if (getJsonByteSize(payload) > PENDO_LIMIT_BYTES) {
    const fallbackPayload: { event: string; timestamp: number; [key: string]: unknown } = {
      event: eventType,
      errorMsg: 'Error data too large after sanitization',
      timestamp,
    };
    return fallbackPayload;
  }

  return payload;
};

export { truncateToBytes, sanitizeForPendo, getJsonByteSize };
